import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ContestRulesModal from "components/client/ContestRulesModal";
import { apiUrl } from "utils/apiUrl";
import { readClientSession } from "utils/clientSession";
import {
  hasAcceptedContestRules,
  saveContestRulesAcceptance,
} from "utils/contestRulesAcceptance";

export default function ClientContest() {
  const session = readClientSession();
  const clientId =
    session?.clientId != null ? String(session.clientId) : "";

  const [showRulesModal, setShowRulesModal] = useState(false);
  const [country, setCountry] = useState("—");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!clientId) {
      setShowRulesModal(false);
      return;
    }
    setShowRulesModal(!hasAcceptedContestRules(clientId));
  }, [clientId]);

  const load = useCallback(async () => {
    if (!clientId) {
      setLoading(false);
      setError("Sign in to view the contest leaderboard.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await fetch(apiUrl("/api/client/contest/leaderboard"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: Number(clientId) }),
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.message || "Could not load contest data.");
      }
      setCountry(String(data.country || "—"));
      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch {
      setCountry("—");
      setRows([
        {
          rank: 1,
          country: "—",
          clientId,
          referrals: 0,
          isYou: true,
        },
      ]);
      setError("");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (showRulesModal) return;
    void load();
  }, [load, showRulesModal]);

  function handleRulesAccepted() {
    saveContestRulesAcceptance(clientId);
    setShowRulesModal(false);
  }

  return (
    <>
      {showRulesModal ? <ContestRulesModal onAccept={handleRulesAccepted} /> : null}

      <div className={showRulesModal ? "pointer-events-none select-none blur-sm" : ""}>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-navy-700 dark:text-white">Contest</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Country leaderboard — one edition per country. Top 5 qualified referrers win
              subscription prizes.
            </p>
          </div>

          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800 sm:p-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Your account country
            </p>
            <p className="mt-2 text-xl font-bold text-navy-700 dark:text-white">{country}</p>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Rankings below are for this country only.{" "}
              <Link
                to="/Contest/RefferalContest"
                className="font-semibold text-brand-500 hover:text-brand-600 dark:text-brand-300"
              >
                Read contest rules
              </Link>
            </p>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800">
            <div className="border-b border-gray-100 px-5 py-4 dark:border-white/10 sm:px-6">
              <h2 className="text-lg font-bold text-navy-700 dark:text-white">Leaderboard</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Sorted by validated referrals (highest first)
              </p>
            </div>

            {loading ? (
              <p className="px-5 py-10 text-center text-sm text-gray-500 dark:text-gray-400 sm:px-6">
                Loading leaderboard…
              </p>
            ) : error ? (
              <p className="px-5 py-10 text-center text-sm text-red-600 dark:text-red-400 sm:px-6">
                {error}
              </p>
            ) : rows.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-gray-500 dark:text-gray-400 sm:px-6">
                No leaderboard data yet for your country.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/80 text-xs uppercase tracking-wide text-gray-500 dark:border-white/10 dark:bg-navy-900/50 dark:text-gray-400">
                      <th className="px-5 py-3 font-semibold sm:px-6">Rank</th>
                      <th className="px-5 py-3 font-semibold sm:px-6">Country</th>
                      <th className="px-5 py-3 font-semibold sm:px-6">ClientID</th>
                      <th className="px-5 py-3 font-semibold sm:px-6">Referrals</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => {
                      const rank = row.rank != null ? row.rank : index + 1;
                      return (
                        <tr
                          key={`${row.clientId}-${rank}`}
                          className={`border-b border-gray-50 last:border-0 dark:border-white/5 ${
                            row.isYou ? "bg-brand-500/5 dark:bg-brand-400/10" : ""
                          }`}
                        >
                          <td className="px-5 py-3.5 font-semibold tabular-nums text-navy-800 dark:text-white sm:px-6">
                            #{rank}
                            {row.isYou ? (
                              <span className="ml-2 text-xs font-bold uppercase text-brand-500 dark:text-brand-300">
                                You
                              </span>
                            ) : null}
                          </td>
                          <td className="px-5 py-3.5 text-gray-700 dark:text-gray-200 sm:px-6">
                            {row.country || country}
                          </td>
                          <td className="px-5 py-3.5 font-semibold tabular-nums text-navy-800 dark:text-white sm:px-6">
                            {row.clientId}
                          </td>
                          <td className="px-5 py-3.5 tabular-nums font-bold text-emerald-600 dark:text-emerald-400 sm:px-6">
                            {Number(row.referrals) || 0}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}
